export class Samples {
  public static readonly TABLE_SORT_PIPE = `
  /**
 * usage:
 * <tr *ngFor="let row of rows | tableSort: targetProp:order">
 *
 * Orders by item[index], order is either 'asc' or 'desc'.
 * Detects date format m/d/yy. Will mutate invalid date format into
 * a format the Date object can parse correctly. The Dates are then
 * ordered.
 */

import { Pipe, PipeTransform } from '@angular/core';
import * as _ from 'lodash';

@Pipe({
  name: 'tableSort',
  pure: false
})
export class TableSortPipe implements PipeTransform {

  // yearPrefix is prefix of year assumed to be 20, i.e 20 + 16.
  // Date objects need the mm - 1 to parse correct month jan is 00.
  transform(items: any[], index: number, order: string): any {
    let regex = /^([1-9]|1[012])[- /.]([1-9]|[12][0-9]|3[01])[- /.]\\d\\d$/;
    let yearPrefix = '20';
    return _.orderBy(items, [(item) => {
      if(typeof item[index] != 'undefined') {
        if(regex.test(item[index])) {
          let from = item[index].split("/");
          from[2] = yearPrefix + from[2];
          return new Date(from[2], from[0] - 1, from[1]);
        }
        if(typeof item[index] === 'number') {
          return item[index];
        }
        return item[index].toLowerCase();
      }
    }],[order]);
  }

}`;

  public static readonly INPUT_FILTER_PIPE = `
    /**
 * Usage:
 * *ngFor="let row of rows | inputFilter:inputString"
 *
 * inputString is tied to an input element. Items are an array of items passed
 * to the pipe.
 *
 * If the text input string args matches any character in any of the
 * elements of the [item] the item is pushed to _item, breaks out of the loop to
 * get next item. If _items are empty after processing items. Returns 'No Data'.
 * _items are returned else just return the items.
 */

import {Pipe,PipeTransform} from '@angular/core';

@Pipe({
  name: 'inputFilter'
})

export class InputFilterPipe implements PipeTransform {
  transform(items: any[], args: string): any {
    if (items.length !== 0 && args) {
      let _items = [];
      for (let i = 0; items.length > i; i++) {
        let item = items[i];
        for (let j = 0; item.length > j; j++) {
          let el = item[j];
          if (el.toLowerCase().indexOf(args.toLowerCase()) !== -1) {
            _items.push(item);
            break;
          }
        }
      }
      if(_items.length === 0) {
        _items.push(['', 'No Data']);
      }
      return _items;
    }
    return items;
  }
}`;

  public static readonly BREADCRUMB_COMPONENT_TS = `
    /**
 * Can't use a breadcrumb in the traditional sense(child routes). I needed a breadcrumb that would follow a trail
 * of sibling routes.
 */
/*
usage:
       *****for every component that has a route and breadcrumb insert this: ***********

       constructor(private crumb: BreadcrumbService, private route: ActivatedRoute) { }

       ngOnInit() {
       this.crumb.setCurrentRoute(this.route);
       }
       *********************************************************************************

       Sets the current url in the current breadcrumb. Removes 1 url segment then sets next url i.e
       current breadcrumb -> http://localhost/initiatives/1/overall
       current breadcrumb - 1 -> http://localhost/initiatives/1/
       current breadcrumb - 2 -> http://localhost/initiatives/
       current breadcrumb - 3 -> http://localhost/

       data key in route config object:
         data: {
           breadcrumb: {
           parent: '',
           label: 'Initiative Status',
           segments: 2,
           url: 'initiatives',
           }
         }
 */

import {Component, OnInit, OnDestroy} from '@angular/core';
import {ActivatedRoute, NavigationEnd, Router} from "@angular/router";
import {BreadcrumbService} from "./services/breadcrumb.service";
import { Location } from '@angular/common'
import {Subscription} from "rxjs";

// label: Title of Breadcrumb,
// parent - component name of parent breadcrumb, if last in line gets parent: ''
// segments - number of segments that makeup the current breadcrumb, this number of segments will be removed
// i.e segments = 2, init/initId/chart/1 => init/initId chart and 1 removed
// url - blank, will be filled in at runtime. url of current breadcrumb
interface IBreadcrumb {
  label: string;
  parent: string,
  segments?: number;
  url: string;
}

@Component({
  selector: 'ap-breadcrumb',
  template: \`
    <ul class="pl-bc">
      <li *ngFor="let breadcrumb of breadcrumbs; let isLast = last" [class.elDisabled]="isLast">
        <a [routerLink]="[breadcrumb.url]">{{breadcrumb.label}}</a>
      </li>
    </ul>
  \`,
  styles: [\`
    .elDisabled a {
      pointer-events: none;
      cursor: default;
      color: #3f3f3f;
    }
  \`]
})
export class BreadcrumbComponent implements OnInit, OnDestroy {
  public breadcrumbs: IBreadcrumb[];
  private routerConfig: any;
  private routeData: any;
  private url: any;
  private urlArray: any;
  private unsub: Subscription;

  /* @class DetailComponent
  * @constructor
  */
  constructor(
    private currentRoute: ActivatedRoute,
    private router: Router,
    private crumb: BreadcrumbService,
    private location: Location
  ) {
    this.breadcrumbs = [];
  }

  ngOnInit() {
    this.routerConfig = this.router.config;

    //subscribe to the NavigationEnd event
    //gets the current route and sets the breadcrumb for the route, pushes it to
    // the breadcrumb array, calls getBreadcrumb to get parent breadcrumbs
    this.unsub = this.router.events.filter(event => event instanceof NavigationEnd).subscribe(() => {
      this.breadcrumbs = [];
      let breadcrumb: IBreadcrumb;
      let bcrumb;
      this.prepareUrl(this.location.prepareExternalUrl(this.location.path()));
      this.currentRoute = this.crumb.getCurrentRoute();
      this.routeData = this.currentRoute.snapshot.data;
      if(this.routeData.hasOwnProperty('breadcrumb')) {
        bcrumb = this.routeData.breadcrumb;
        breadcrumb = this.setBreadcrumb(bcrumb);
      }
      else {
        breadcrumb = this.setBreadcrumb({label: 'No Breadcrumb', params: '', url: ''});
        this.breadcrumbs.push(breadcrumb);
        return;
      }
      this.breadcrumbs.push(breadcrumb);
      this.getBreadcrumbs(this.routeData);
    });
  }

  /**
   * Gets the breadcrumb trail for the current view. Takes the data object from the current route and
   * gets the next route in the trail specified by parent key in the breadcrumb object inside
   * the router config object. Recursive function. Will call itself as many times as there is a parent defined for the
   * current route config object.
   * @param data object
   */
  getBreadcrumbs(data: any) {
    let breadcrumb: IBreadcrumb;
    let bcrumb;
    let parent;
    if(!data.breadcrumb) {
      return;
    }
    parent = data.breadcrumb.parent || '';
    //match parent name with component name in routerConfig and get the next router config object
    this.routerConfig.forEach((config) => {
      if(config.component) {
        if(config.component.name === parent) {
          if(config.data && config.data.breadcrumb) {
            bcrumb = config.data.breadcrumb;
            breadcrumb = this.setBreadcrumb(bcrumb);
            this.breadcrumbs.unshift(breadcrumb);
            this.getBreadcrumbs(config.data);
          }
        }
      }
    });
  }

  /**
   * Populates the IBreadcrumb object of type IBreadcrumb defined at the
   * top of this component.
   * @param data
   * @returns {IBreadcrumb}
   */
  setBreadcrumb(data: any): IBreadcrumb {
    let breadcrumb: IBreadcrumb = {
      label: data.label,
      parent: data.parent,
      segments: data.segments,
      url: ''
    };
    breadcrumb.url = this.setURL(breadcrumb.segments);
    return breadcrumb;
  }

  /**
   * Removes the '#' from the url when returned from the location variable
   * @param url
   */
  prepareUrl(url) {
    this.urlArray = url.split('/');
    this.urlArray.shift();
  }

  /**
   * sets the url for the breadcrumb by concatenating the elements from the urlArray after
   * the '#' has been removed. Then removes the designated number of elements from the array to prepare the path
   * for the next url in the breadcrumb trail. Default number to be removed is 1.
   * @returns {any}
   */
  setURL(segments = 1) {
    let newUrl = this.urlArray.join('/');
    this.urlArray.splice(-segments, segments);
    return newUrl;
  }

  ngOnDestroy() {
    this.unsub.unsubscribe();
  }

}`;

  public static readonly BREADCRUMB_SERVICE = `
import { Injectable } from '@angular/core';
import {ActivatedRoute} from "@angular/router";

@Injectable()
export class BreadcrumbService {
  private current_route: ActivatedRoute;

  constructor() { }

  setCurrentRoute(route: ActivatedRoute) {
    this.current_route = route.pathFromRoot[route.pathFromRoot.length - 1];
  }

  getCurrentRoute() {
    return this.current_route;
  }

}`;

  public static readonly ERROR_COMPONENT = `
    /**
 * Displays an error to the user if there is an http error. Shows the error message
 * and the error code.
 *
 * usage: <ap-error *ngIf="err.hasError" [hasError]="err"></ap-error>
 *
 * in the component:
 *
 * export class Component {
 * private err = {
    errorMsg: '',
    hasError: false
  };
 *
 * the observable has the format of:
 * this.Sub = this.data.getData(param)
      .finally(() => {
        this.stopRefreshing(); // spinner off
      })
      .subscribe(
      data => {
        ....
      },
      error => {
        this.err.hasError = true;         //boolean
        this.err.errorMsg = <any>error;   //Error
      }
    );
 * Displays an error to the user if there is an http error. Shows the error message
 * and the error code. If hasError(err.hasError === true) it will set an
 * error msg string and output to the user.
 */
import {Component, Input} from '@angular/core';

@Component({
  selector: 'ap-error',
  template: \`
    <div class="row">
      <div class="column small-12">
        <p class="smoke-wine-text">{{_error}}</p>
      </div>
    </div>
  \`,
  styles: []
})
export class ErrorComponent {
  private errorStr: string = 'There was a problem with the request. Error: ';
  private _error: string;
  constructor() { }

  @Input()
  public set hasError(err: any) {
    if(!err.hasError) {
      return;
    }
    else {
      this._error = this.errorStr + err.errorMsg;
    }
  }
}`;

  public static readonly PIECHART_COMPONENT =  `
    
/*
 usage <wd-piechart [specs]="specs"></wd-piechart>
 piechart object:
   {
     title: "Overall Status",
     subtitle: "144,000 Eligible Accounts",
     centerX: 200,
     centerY: 200,
     radius: 100,
     stroke: "#ffffff",
     strokeWidth: 3,
     slices: [
       {value: 1000, color: '#A62E5C', label: {text: 'North'}},
       {value: 400, color: '#9bc850', label: {text: 'South'}},
       {value: 300, color: '#777777', label: {text: 'East'}},
       {value: 60, color: 'pink', label: {text: 'Middle'}},
       {value: 1, color: 'purple', label: {text: 'West'}}
     ]
   };
 centerX - center point of circle x-plane
 centerY - center point of circle y-plane
 radius - radius of circle
 stroke - pie slice border color
 strokeWidth - border color width
 slices - pie slice data
 Can be shown without a button by using [noButton]="true"
 */
import {Component, OnInit, Input, ViewChild, ElementRef} from '@angular/core';

@Component({
  selector: 'ap-piechart',
  template: \`
    <div [ngClass]="{'no-button': noButton}" class="chart-container">
      <div [ngClass]="{'no-button': noButton}" #svgTitle class="svg-title">
          <h3 class="chart-title">{{specs.title}}</h3>
          <h5 class="chart-sub-title">{{specs.subtitle}}</h5>
        </div>
        <div #svgLegend class="svg-legend">
        <ul class="svg-list">
          <li *ngFor="let slice of specs.slices">
            <div [ngStyle]="{'background-color': slice.color}" class="svg-list-color"></div>
            {{slice.label.text}}
          </li>
        </ul>
        </div>
      <div class="svg-container">
        <svg viewBox="0 0 300 300" preserveAspectRatio="xMidYMid meet">
          <svg:path *ngFor="let slice of specs.slices"
              [attr.d]="slice.svgPath"
              [attr.stroke-width]="3"
              [attr.stroke]="specs.stroke"
              [attr.fill]="slice.color"/>
          <svg:line *ngFor="let slice of specs.slices" 
                [attr.stroke]="'#000000'" 
                [attr.stroke-width]="1"
                [attr.x1]="specs.centerX + slice.ticMark.x" 
                [attr.y1]="specs.centerY + (slice.ticMark.y * -1)"
                [attr.x2]="specs.centerX + slice.ticMark.x + slice.labelLine" 
                [attr.y2]="specs.centerY + (slice.ticMark.y * -1)"
                [attr.transform]="slice.transform"/>
          <svg:text *ngFor="let slice of specs.slices" 
            [attr.x]="specs.centerX + slice.label.x" 
            [attr.y]="specs.centerY + (slice.label.y - slice.label.adjustment) * -1"
            style="font-family: 'Open Sans'; font-size: 10pt" [attr.text-anchor]="slice.label.alignment">{{slice.value}}
          </svg:text>
        </svg>
        </div>
    </div>
    <div [ngClass]="{'no-button': noButton}" class="chart-button">
      <button type="button" [routerLink]="specs.url" class="ap-button">View Report</button>
    </div>
  \`,
  styles: [\`
    /* no button */
    .chart-button.no-button {
      display: none;
    }
    .chart-container.no-button {
      border: none;
    }
    .svg-title.no-button {
      display: none;
    }
    /*************/
    
    .ap-button {
      float: right;
    }
    .chart-title {
      margin-top: 0;
    }
    .chart-sub-title {
      margin-top: 1em;
    }
    .chart-container {
      margin-top: 2em;
      padding: 1em;
      border: 1px solid #e7e7e7;
    }
    .chart-button {
      padding: .7em .7em;
      overflow: hidden;
      border-left: 1px solid #e7e7e7;
      border-right: 1px solid #e7e7e7;
      border-bottom: 1px solid #e7e7e7;
    }
    .svg-container {
      display: inline-block;
      position: relative;
      width: 100%;
      padding-bottom: 90%;
      vertical-align: top;
      overflow: hidden;
    }
    .svg-container svg{
      display: inline-block;
      position: absolute;
      top: 0;
      left: 0;
    }
    .svg-title {
      top: -10px;
    }
    .svg-legend {
       overflow: hidden;
       position: relative;
       top: 1.7em;
       text-align: center;
    }
    .svg-list {
      display: inline-block;
      list-style-type: none;
      margin: 0 .5em;
      padding-left: 0;
    }
    .svg-list li{
      display: inline-block;
      list-style-type: none;
      margin: 0 .5em;
    }
    .svg-list-color {
      display: inline-block;
      height: 6px;
      width: 6px;
      margin-bottom: 2px;
    }
    :host {
      display: block;
    }
  \`]
})
export class PiechartComponent implements OnInit {
  @Input() specs:any;
  @Input() noButton: boolean = false;
  @ViewChild('svgTitle') svgTitle: ElementRef;
  @ViewChild('svgLegend') svgLegend: ElementRef;
  private x1:number;
  private y1:number = 0;
  private total:number = 0;
  private prevEndingAngle:number = 0;
  private maxAngle = 100;

  ngOnInit() {
    //set first x to the radius of the circle
    this.x1 = this.specs.radius;

    //get the total of all values from all slices
    this.specs.slices.forEach((slice) => {
      this.total += slice.value;
    });

    this.specs.slices = this.sortSlices(this.specs.slices);
    //create the pie chart
    this.specs.slices.forEach((slice) => {
      slice['ticMark'] = {};
      slice['x1'] = this.x1;
      slice['y1'] = this.y1;

      //slice value/sum total of all slices * 360 to get slice angle
      //then add the previous ending angle to get the angle at which to start drawing the slice
      slice['endingAngle'] = ((slice.value/this.total) * 360) + this.prevEndingAngle;
      //angles must be converted to radians before being used to set the point
      //on the circle
      let radians = slice['endingAngle'] * (Math.PI/180);
      slice['x2'] = (Math.cos(radians) * this.specs.radius);
      slice['y2'] = (Math.sin(radians) * this.specs.radius);
      this.x1 = slice['x2'];
      this.y1 = slice['y2'];
      slice['isObtuse'] = this.isObtuse(slice['endingAngle'], this.prevEndingAngle);
      this.prevEndingAngle = slice['endingAngle'];
    });

    //create the line and text placement for the graph
    //gets the center point and angle of the center point for each pie slice
    //checks if slice is large enough to contain the text and if not it
    //draws the tick mark at that angle and sets the text at the end of the drawn line
    //otherwise it sets the text within the pie slice
    this.specs.slices.forEach((slice, i) => {
      let angle = i == 0 ? slice['endingAngle'] / 2 : slice['endingAngle'] - (slice['endingAngle'] - this.prevEndingAngle) / 2;
      let radians = angle * (Math.PI/180);
      let ticMarkX = Math.cos(radians) * this.specs.radius;
      let ticMarkY = Math.sin(radians) * this.specs.radius;
      slice.label['x'] = Math.cos(radians) * (this.specs.radius + this.labelPlacement(slice['endingAngle'], this.prevEndingAngle, i));
      slice.label['y'] = Math.sin(radians) * (this.specs.radius + this.labelPlacement(slice['endingAngle'], this.prevEndingAngle, i));
      slice.labelLine = this.textInside(slice['endingAngle'], this.prevEndingAngle, i);
      slice.label['alignment'] = this.getAlignment(angle);
      slice.label['adjustment'] = this.getAdjustment(angle);
      slice['ticMark'] = {x:ticMarkX, y:ticMarkY, rotation:angle};

      slice['svgPath'] = "M " + this.specs.centerX + "," + this.specs.centerX + " l " + slice['x1'] + "," + slice['y1'] * -1 + " a" + this.specs.radius + "," + this.specs.radius + " 0 " + slice['isObtuse'] + " 0 " + (slice['x2'] - slice['x1']) + "," + ((slice['y2'] - slice['y1']) * -1);
      slice['transform'] = "rotate(" + (slice['ticMark']['rotation'] * -1) + "," + (this.specs.centerX + slice['ticMark']['x']) + "," + (this.specs.centerX + (slice['ticMark']['y'] * -1)) + ")";
      this.prevEndingAngle = slice['endingAngle'];
    });
  }

  /**
   * Spreads apart objects with small angles between objects with larger angles
   * so that the numbers in the piechart don't overlap.
   * @param slices
   * @returns {any}
   */
  sortSlices(slices) {
    let newSlices = slices,
    len = newSlices.length - 1,
          prevEndingAngle = 0;

    //gets angles for all slices in the pie
    newSlices.forEach((slice) => {
      slice['endingAngle'] = ((slice.value/this.total) * 360) + prevEndingAngle;
      slice['realAngle'] = slice['endingAngle'] - prevEndingAngle;
      prevEndingAngle = slice['endingAngle'];
    });
    //looks for any angles that might be next to each other below 10 degrees
    for(let i = 0; i < len; i++) {
      let currAngle = newSlices[i].realAngle,
          nextAngle = newSlices[i + 1].realAngle;
      if(currAngle < 10 && nextAngle < 10) {
        //if two angles are found next to each other that are < 10 degrees
        //looks for two angles that are larger than 10 degrees and puts the
        //smaller angle in between
        for(let j = 0; j < len; j++) {
          let current = newSlices[j].realAngle,
              next = newSlices[j + 1].realAngle;
          if(current > 10 && next > 10) {
            let moved = newSlices.splice(i, 1);
            newSlices.splice(j+1, 0, moved[0]);
          }
        }
      }
    }
    return newSlices;
  }

  /**
   * depending on the angle of the pie slice determines the position
   * of the text at the tick mark
   * @param angle
   * @returns {any}
   */
  getAlignment(angle) {
    if(angle < 45) { return "start";}
    else if(angle < 135) {return "middle";}
    else if(angle < 225) {return "end";}
    else if(angle < 315) {return "middle";}
    else {return "start";}
  }

  /**
   * For angles bewteen 225 and 315 the label needs to
   * be moved according to the texts width away from
   * the end of the drawn tick mark to prevent overlap
   * @param angle
   * @returns {number}
   */
  getAdjustment(angle) {
    return angle > 225 && angle < 315 ? 15 : 0;
  }

  //check for angles > 180deg
  //sets the large arc flag to 1 if > 180deg
  isObtuse(start, end) {
    return (start - end) > 180 ? 1:0;
  }

  //for any angle > 100deg the text will be set inside the pie slice
  //gets the absolute value of the angle
  labelPlacement(angleEnd, angleStart, i) {

    let angle = i == 0 ? angleEnd : Math.abs(angleStart - angleEnd);
    return (angle > this.maxAngle) ? -55 : 20;
  }

  //if the angle is > 100 will prevent a tick mark being drawn
  //by setting the x1, y1 - x2, y2 points to the same point
  //gets the absolute value of the angle
  textInside(angleEnd, angleStart, i) {
    let angle = i == 0 ? angleEnd : Math.abs(angleStart - angleEnd);
    return (angle > this.maxAngle) ? 0 : 20;
  }

}`;

  public static readonly ACCORDIAN_GROUP_COMPONENT = `
    /*
 usage:
 <ap-accordian>
 <ap-accordian-group accordianId="1" heading="Advanced Search" [isOpen]="true">
 <ap-adv-search></ap-adv-search>
 </ap-accordian-group>
 </ap-accordian>
 *
 * ap-accordian-group defines a section of an accordian. To be used within ap-accordian.
 */
import {Component, OnInit, Input, OnDestroy} from '@angular/core';
import {AccordianService} from "./services/accordian.service";
import {Subscription} from "rxjs";

@Component({
  selector: 'ap-accordian-group',
  template: \`
    <li class="accordion-navigation" [ngClass]="{'panel-open': _isOpen, 'active': _isOpen}">
      <a (click)="toggleOpen($event)" href="#">
        <div class="accordion-arrow__container"><i class="icon-rws-down-caret accordion-arrow"></i></div>
        <div class="accordion__title">{{heading}}</div>
      </a>
      <!--Accordion Content-->
      <div class="content bkgrd" [ngClass]="{'active': _isOpen}" [hidden]="!_isOpen">
        <ng-content></ng-content>
      </div>
    </li>
  \`,
  styles: [\`
    .content.bkgrd.active {
      background-color: #f5f5f5;
    }
  \`]
})
export class AccordianGroupComponent implements OnInit, OnDestroy {
  private _isOpen:boolean = false;
  private accordion;
  @Input() heading: string;
  @Input() accordianId: number;
  @Input() isOpen: boolean;


  constructor(private accService: AccordianService) { }
  private accUnsub: Subscription;

  ngOnInit() {
    this.accUnsub = this.accService.accCloseOthers$.subscribe(
      data => this.closeOthers(data)
    )
    if(this.isOpen) {
      this._isOpen = this.isOpen;
    }
  }

  /**
   * Toggles open a section and closes others.
   * @param event
   */
  toggleOpen(event) {
    event.preventDefault();
    this.accService.closeOthers(this);
  }

  /**
   * Closes other accordian sections with the same accordian id.
   * @param group
   */
  closeOthers(group) {
    if(group.accordianId === this.accordianId) {
      if(group != this) {
        this._isOpen = false;
      }
      else {
        if(this._isOpen === true) {
          this._isOpen = false;
          return;
        }
        this._isOpen = true;
      }
    }
  }

  ngOnDestroy() {
    this.accUnsub.unsubscribe();
  }


}`;

  public static readonly ACCORDIAN_COMPONENT = `
    /**
 * usage:
 *<ap-accordian>
 <ap-accordian-group accordianId="1" heading="Advanced Search" [isOpen]="true">
 <ap-adv-search></ap-adv-search>
 </ap-accordian-group>
 </ap-accordian>
 *
 * This is just a container for an ap-accordian-group. It can contain as many
 * ap-accordian-groups as needed. Takes an accordianId, for use when there are more than one accordian
 * on the same page. isOpen - section of accordian is open by default. heading - title of accordian.
 */
import {Component} from '@angular/core';

@Component({
  selector: 'ap-accordian',
  template: \`
    <div class="pl-accor">
      <ul class="accordion" data-accordion="">
        <ng-content></ng-content>
      </ul>
    </div>
  \`,
  styles: []
})
export class AccordianComponent {

}`;

}



// WEBPACK FOOTER //
// ./src/app/shared/classes/Samples.ts